import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { FilterOutlined } from "@ant-design/icons";
import { Form, Input, Select } from "antd";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Modal,
  Nav,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import FormItem from "@/common/FormItem/FormItem";
import PageTitle from "@/common/PageTitle/PageTitle";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import { useCanViewPage } from "@/contexts/SuperAdminIdentityContext";
import {
  MusicUploadReviewRow,
  OversightBucket,
  useGetMusicUploadReviewQuery,
  useListMusicUploadReviewsQuery,
} from "@/features/music-upload-reviews/musicUploadReviewsApi";
import {
  OVERSIGHT_VIEWS,
  oversightBadgeVariant,
  primaryOversightAction,
  processingJobPath,
  trackPath,
} from "@/features/music-upload-reviews/oversightDisplay";
import styles from "./music-upload-reviews.module.scss";

const PAGE_ID = "uploader";
const PAGE_SIZE = 25;

type SortOrder = "newest" | "oldest";

function formatWhen(iso: string | null | undefined) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatBytes(n: number | null | undefined) {
  if (n == null || !Number.isFinite(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function providerKey(row: MusicUploadReviewRow) {
  return row.sourceMasterAccountId || row.companyName || "";
}

function providerLabel(row: MusicUploadReviewRow) {
  return row.companyName || row.sourceMasterAccountId || "Unknown provider";
}

function processingStateLabel(row: MusicUploadReviewRow) {
  const state = row.processingJob?.state;
  if (state) return state;
  if (row.bridgeStatus) return row.bridgeStatus;
  return "—";
}

const MusicProviderUploadOversightPage: React.FC = () => {
  const canView = useCanViewPage(PAGE_ID);
  const [oversightBucket, setOversightBucket] = useState<OversightBucket>("all");
  const [providerFilter, setProviderFilter] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");
  const [resetCounter, setResetCounter] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } =
    useListMusicUploadReviewsQuery(
      { status: "all", oversightBucket },
      { skip: !canView },
    );

  const { data: detail, isFetching: detailFetching } = useGetMusicUploadReviewQuery(
    selectedId || "",
    { skip: !canView || !selectedId || !showDetailsModal },
  );

  const providerOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of data?.uploads || []) {
      const key = providerKey(row);
      if (!key) continue;
      if (!map.has(key)) map.set(key, providerLabel(row));
    }
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [data?.uploads]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = [...(data?.uploads || [])];
    if (providerFilter) {
      list = list.filter((row) => providerKey(row) === providerFilter);
    }
    if (q) {
      list = list.filter((row) => {
        const hay = `${row.fileName || ""} ${row.uploadId || ""} ${
          row.processingJobId || ""
        } ${row.companyName || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    list.sort((a, b) => {
      const av = String(a.createdAt || "");
      const bv = String(b.createdAt || "");
      return sortOrder === "newest" ? bv.localeCompare(av) : av.localeCompare(bv);
    });
    return list;
  }, [data?.uploads, providerFilter, search, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE) || 1);

  useEffect(() => {
    setPage(1);
  }, [oversightBucket, providerFilter, search, sortOrder]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, page]);

  const selectedRow = useMemo(
    () =>
      rows.find((r) => r.uploadId === selectedId) ||
      detail?.upload ||
      null,
    [rows, selectedId, detail],
  );

  const openDetails = (uploadId: string) => {
    setSelectedId(uploadId);
    setShowDetailsModal(true);
  };

  const closeDetails = () => {
    setShowDetailsModal(false);
  };

  const handleReset = () => {
    setOversightBucket("all");
    setProviderFilter(undefined);
    setSearch("");
    setSortOrder("newest");
    setPage(1);
    setResetCounter((c) => c + 1);
    closeDetails();
    setSelectedId(null);
  };

  if (!canView) {
    return (
      <Container fluid className={styles.container}>
        <div className="alert alert-warning mb-0">
          You do not have permission to view Song Intake.
        </div>
      </Container>
    );
  }

  const job = selectedRow?.processingJob || null;
  const primary = selectedRow ? primaryOversightAction(selectedRow) : null;

  return (
    <Container fluid className={styles.container}>
      <Card style={{ borderRadius: 12 }} data-testid="mp-oversight-card">
        <Card.Body>
          <Card.Title
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <PageTitle title="Song Intake" />
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => refetch()}
                disabled={isFetching}
                style={{
                  background: "white",
                  color: "#333",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontWeight: 500,
                }}
              >
                Refresh
              </Button>
              <Button
                onClick={handleReset}
                style={{
                  background: "white",
                  color: "#333",
                  border: "1px solid #e0e0e0",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontWeight: 500,
                }}
              >
                <FilterOutlined /> Reset All
              </Button>
            </div>
          </Card.Title>

          <p className={styles.helperText}>
            Monitor incoming songs and review any items that need attention.
          </p>

          <Nav
            variant="tabs"
            className="mb-3"
            data-testid="mp-oversight-tabs"
          >
            {OVERSIGHT_VIEWS.map((view) => (
              <Nav.Item key={view.key}>
                <Nav.Link
                  active={oversightBucket === view.key}
                  onClick={() => {
                    setOversightBucket(view.key);
                    closeDetails();
                    setSelectedId(null);
                  }}
                >
                  {view.label}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          <Form layout="vertical" className={styles.form}>
            <Row className="mb-2 g-3 align-items-end">
              <Col md={4}>
                <FormItem
                  label="Music Provider"
                  className="customSelectWrapper"
                  layout="vertical"
                >
                  <Select
                    key={`provider-reset-${resetCounter}`}
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    className={`customSelect ${styles.selectField}`}
                    placeholder="All providers"
                    value={providerFilter}
                    onChange={(value) => setProviderFilter(value || undefined)}
                    options={providerOptions}
                  />
                </FormItem>
              </Col>
              <Col md={4}>
                <FormItem
                  label="Search"
                  className="customSelectWrapper"
                  layout="vertical"
                >
                  <Input.Search
                    key={`search-reset-${resetCounter}`}
                    allowClear
                    className={styles.selectField}
                    placeholder="Filename, upload ID, or job ID"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </FormItem>
              </Col>
              <Col md={4}>
                <FormItem
                  label="Sort"
                  className="customSelectWrapper"
                  layout="vertical"
                >
                  <Select
                    key={`sort-reset-${resetCounter}`}
                    className={`customSelect ${styles.selectField}`}
                    value={sortOrder}
                    onChange={(value) => setSortOrder(value as SortOrder)}
                    options={[
                      { value: "newest", label: "Newest first" },
                      { value: "oldest", label: "Oldest first" },
                    ]}
                  />
                </FormItem>
              </Col>
            </Row>
          </Form>

          {isLoading ? (
            <div className="py-5 text-center">
              <Spinner animation="border" />
            </div>
          ) : isError ? (
            <div className="alert alert-danger mb-0">
              {(error as any)?.data?.message ||
                (error as any)?.message ||
                "Failed to load Song Intake"}
            </div>
          ) : (
            <>
              <div className="small text-muted mb-2">
                Showing {pageRows.length} of {rows.length}
                {rows.length !== (data?.uploads?.length || 0)
                  ? ` (filtered from ${data?.uploads?.length || 0})`
                  : ""}
              </div>

              <Table
                hover
                responsive
                className="legacy-row-height-table align-middle"
                data-testid="mp-oversight-table"
              >
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Music Provider</th>
                    <th>Uploaded</th>
                    <th>Status</th>
                    <th>Processing</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-muted text-center py-4">
                        No uploads for this view.
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((row) => {
                      const rowAction = primaryOversightAction(row);
                      const jobHref = processingJobPath(
                        row.processingJobId || row.processingJob?.jobId,
                      );
                      return (
                        <tr key={row.uploadId}>
                          <td>
                            <div>{row.fileName || "—"}</div>
                            <div className={styles.fileMeta}>
                              {row.detectedAudioFormat || "—"} ·{" "}
                              {formatBytes(row.fileSizeBytes)}
                            </div>
                          </td>
                          <td>{providerLabel(row)}</td>
                          <td>{formatWhen(row.createdAt)}</td>
                          <td>
                            <Badge bg={oversightBadgeVariant(row.oversightBucket)}>
                              {row.displayLabel ||
                                row.oversightBucket ||
                                row.status ||
                                "—"}
                            </Badge>
                          </td>
                          <td>{processingStateLabel(row)}</td>
                          <td>
                            <Button
                              variant="link"
                              className={styles.actionLink}
                              onClick={() => openDetails(row.uploadId)}
                            >
                              Details
                            </Button>
                            {jobHref &&
                            (rowAction.kind === "open-job" ||
                              rowAction.kind === "review-policy" ||
                              rowAction.kind === "review-metadata") ? (
                              <Button
                                as={Link as any}
                                to={jobHref}
                                variant="link"
                                className={styles.actionLink}
                              >
                                {rowAction.label}
                              </Button>
                            ) : null}
                            {rowAction.kind === "security-details" ? (
                              <span className="text-danger small">
                                No admit override
                              </span>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>

              <PaginationRow
                page={page}
                totalPages={totalPages}
                onPageChange={(p) => setPage(p)}
                onClickPrevious={() => setPage((p) => Math.max(1, p - 1))}
                handleOnClickNext={() =>
                  setPage((p) => Math.min(totalPages, p + 1))
                }
                disableLeft={page <= 1}
                disableNext={page >= totalPages}
              />
            </>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showDetailsModal}
        onHide={closeDetails}
        size="lg"
        data-testid="mp-oversight-details-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Upload details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailFetching && !selectedRow ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : !selectedRow ? (
            <p className="text-muted mb-0">Upload not found.</p>
          ) : (
            <>
              {selectedRow.attentionKind === "staging-auto-admit-gate" ? (
                <div className="alert alert-warning py-2 small">
                  Bridged through canonical checks. Track/AAV withheld by staging
                  auto-admit gate.
                </div>
              ) : null}
              {selectedRow.oversightBucket === "flagged-security" ? (
                <div className="alert alert-danger py-2 small">
                  Security / malware outcome — quarantined. No admit override.
                </div>
              ) : null}

              <dl className={`row small ${styles.detailDl}`}>
                <dt className="col-sm-4">Provider</dt>
                <dd className="col-sm-8">
                  {selectedRow.companyName || "—"}
                  <div className="text-muted">
                    {selectedRow.sourceMasterAccountId}
                  </div>
                </dd>
                <dt className="col-sm-4">Upload ID</dt>
                <dd className="col-sm-8">
                  <code className="small">{selectedRow.uploadId}</code>
                </dd>
                <dt className="col-sm-4">Filename</dt>
                <dd className="col-sm-8">{selectedRow.fileName}</dd>
                <dt className="col-sm-4">Uploaded</dt>
                <dd className="col-sm-8">{formatWhen(selectedRow.createdAt)}</dd>
                <dt className="col-sm-4">Lifecycle</dt>
                <dd className="col-sm-8">
                  <Badge bg={oversightBadgeVariant(selectedRow.oversightBucket)}>
                    {selectedRow.displayLabel || selectedRow.oversightBucket}
                  </Badge>
                  {primary ? (
                    <div className="text-muted mt-1">{primary.label}</div>
                  ) : null}
                </dd>
                <dt className="col-sm-4">Bridge</dt>
                <dd className="col-sm-8">
                  {selectedRow.bridgeStatus || "—"}
                  {selectedRow.bridgedAt
                    ? ` · ${formatWhen(selectedRow.bridgedAt)}`
                    : ""}
                  {selectedRow.bridgeError ? (
                    <div className="text-danger">{selectedRow.bridgeError}</div>
                  ) : null}
                </dd>
                <dt className="col-sm-4">Processing Job</dt>
                <dd className="col-sm-8">
                  {selectedRow.processingJobId ? (
                    <Link
                      to={
                        processingJobPath(selectedRow.processingJobId) || "#"
                      }
                    >
                      <code className="small">
                        {selectedRow.processingJobId}
                      </code>
                    </Link>
                  ) : (
                    "—"
                  )}
                </dd>
                <dt className="col-sm-4">Processing state</dt>
                <dd className="col-sm-8">{job?.state || "—"}</dd>
                <dt className="col-sm-4">Malware / security</dt>
                <dd className="col-sm-8">{job?.malwareStatus || "—"}</dd>
                <dt className="col-sm-4">Metadata</dt>
                <dd className="col-sm-8">
                  {job?.discoveredTitle || job?.discoveredArtist
                    ? `${job?.discoveredTitle || "—"} / ${
                        job?.discoveredArtist || "—"
                      }`
                    : "—"}
                </dd>
                <dt className="col-sm-4">Policy</dt>
                <dd className="col-sm-8">
                  {job?.policyStatus || "—"}
                  {job?.reasonCode ? (
                    <div className="text-muted">{job.reasonCode}</div>
                  ) : null}
                </dd>
                <dt className="col-sm-4">Catalog link</dt>
                <dd className="col-sm-8">{job?.catalogLinkStatus || "—"}</dd>
                <dt className="col-sm-4">Track</dt>
                <dd className="col-sm-8">
                  {job?.trackId ? (
                    <Link to={trackPath(job.trackId) || "#"}>
                      <code className="small">{job.trackId}</code>
                    </Link>
                  ) : (
                    "—"
                  )}
                </dd>
                <dt className="col-sm-4">AAV</dt>
                <dd className="col-sm-8">
                  {job?.approvedArtifactVersionId ? (
                    <code className="small">
                      {job.approvedArtifactVersionId}
                    </code>
                  ) : (
                    "—"
                  )}
                </dd>
                <dt className="col-sm-4">Hold / block reason</dt>
                <dd className="col-sm-8">
                  {job?.reasonCode ||
                    selectedRow.rejectionReason ||
                    selectedRow.attentionKind ||
                    "—"}
                </dd>
                <dt className="col-sm-4">Holding object</dt>
                <dd className="col-sm-8">
                  <code className="small">{selectedRow.s3Key || "—"}</code>
                </dd>
                <dt className="col-sm-4">Processing intake</dt>
                <dd className="col-sm-8">
                  <code className="small">
                    {selectedRow.processingObjectKey || "—"}
                  </code>
                </dd>
              </dl>

              <h3 className={styles.detailSectionTitle}>Audit history</h3>
              {(detail?.reviewHistory || []).length === 0 ? (
                <p className="small text-muted mb-0">No audit events yet.</p>
              ) : (
                <ul className="list-unstyled small mb-0">
                  {(detail?.reviewHistory || []).map((ev) => (
                    <li
                      key={
                        ev.eventId || `${ev.eventType}-${ev.eventTimestamp}`
                      }
                      className="border-bottom py-2"
                    >
                      <div className="fw-semibold">
                        {ev.eventType}{" "}
                        <span className="text-muted fw-normal">
                          {formatWhen(ev.eventTimestamp)}
                        </span>
                      </div>
                      <div className="text-muted">
                        {ev.beforeStatus || "—"} → {ev.afterStatus || "—"}
                        {ev.actorSub ? ` · ${ev.actorSub}` : ""}
                      </div>
                      {ev.rejectionReason ? (
                        <div>Reason: {ev.rejectionReason}</div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {selectedRow?.processingJobId &&
          primary &&
          (primary.kind === "open-job" ||
            primary.kind === "review-policy" ||
            primary.kind === "review-metadata") ? (
            <Button
              as={Link as any}
              to={processingJobPath(selectedRow.processingJobId) || "#"}
              variant="primary"
            >
              {primary.label}
            </Button>
          ) : null}
          <Button variant="secondary" onClick={closeDetails}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(MusicProviderUploadOversightPage, {
  onRedirecting: () => (
    <div className="p-5 text-center">
      <Spinner animation="border" />
    </div>
  ),
});
